import React from "react";
import Skeleton from "react-loading-skeleton";
import GridWrapper from "../../../components/GridWrapper";
import { FormSelect } from "../../../components/forms";
import { ErrorMessage, ErrorMessageWrapper } from "../../../components/messages";
import { patchReplace } from "../../../helpers/patchDoc";
import useProductSelection from "../hooks/useProductSelection";
import FillFromValuationsButton from "./FillFromValuationsButton";
import ProductCostsPopup from "./ProductCostsPopup";

const ProductSelection = ({
    productLabel = "Product:",
    designationLabel = "Designation:",
    providerLabel = "Provider Reference:",
    custodyLabel = "Custody Reference:",
    changeOwner,
    isInvest
}) => {
    const {
        instructionId,
        instruction,
        instructionSection,
        invest,
        divest,
        productOptions,
        isLoadingProductOptions,
        productObjects,
        isFetchingProductObjects,
        designationOptions,
        providerOptions,
        custodyOptions,
        isFetchingProviders,
        isUninitializedProviders,
        realTimeProductId,
        realTimeDesignationId,
        realTimeProviderRef,
        isLoading,
        isFetchingDesignations,
        isError,
        lastDesignationsArg,
        setRealTimeProductId,
        setRealTimeDesignationId,
        addCustomProvider,
        addCustomCustodian,
        patchService,
        onBlur,
        refetch
    } = useProductSelection({ isInvest, changeOwner });

    if (isError)
        return <ErrorMessageWrapper margin="0px auto" maxWidth="500px">
            <ErrorMessage
                message="Sorry! We were unable to load the products for this instruction type. Please try again"
                retryCallback={refetch}
            />
        </ErrorMessageWrapper>

    return <GridWrapper gridTemplateColumns={"5fr 4fr repeat(2, 2fr) auto"}>
        <GridWrapper gridTemplateColumns={"1fr auto"} gridRowGap="0px">
            {isLoading
                ? <Skeleton />
                : <FormSelect
                    label={productLabel}
                    options={productOptions}
                    isLoadingOptions={isLoadingProductOptions}
                    isDisabled={isLoadingProductOptions}
                    defaultValue={instructionSection?.productId}
                    onChange={(selection) => setRealTimeProductId(selection.value)}
                    onBlur={(selection) =>
                        new Promise((resolve, reject) => {
                            let operations = [patchReplace("productId", selection.value)];

                            // If the product has changed, the new designations list has loaded, and the designation is invalid: reset the designationId
                            if (selection.value === lastDesignationsArg?.productId && !isFetchingDesignations) {
                                if (!designationOptions?.some(({ value }) => value === instructionSection?.designationId))
                                    operations.push(patchReplace("designationId", null));
                            }

                            // If the product has changed and the custody ID was from the previous product: update the custody ID to the new product's default custodian
                            if (!isFetchingProductObjects) {
                                const { defaultCustodianId: prevCustodyId } = productObjects?.find(({ id }) => id === instructionSection?.productId) || {};
                                const { defaultCustodianId: newCustodyId } = productObjects?.find(({ id }) => id === selection.value) || {};

                                if (instructionSection?.custodyId == null || (instructionSection?.custodyId === prevCustodyId && prevCustodyId !== newCustodyId))
                                    operations.push(patchReplace("custodyId", newCustodyId));
                            }

                            return patchService(operations)
                                .then(resolve, reject);
                        })}
                />}

            {isLoading
                ? <></>
                : <ProductCostsPopup
                    className={"col-start-2 mt-auto"}
                    instructionId={instructionId}
                    selectedProductId={realTimeProductId}
                    hasProductCosts={(instruction?.productCosts?.length ?? 0) > 0}
                    productName={productOptions?.find(opt => opt.value === invest?.productId)?.label ?? "Selected product"}
                    section={(divest?.id && isInvest) ? "Out" : "In"}
                />}
        </GridWrapper>
        {isLoading
            ? <Skeleton />
            : <FormSelect
                label={designationLabel}
                options={designationOptions ?? []}
                defaultValue={instructionSection?.designationId}
                onChange={(designation) => setRealTimeDesignationId(designation.value)}
                onBlur={(designation) => onBlur("designationId", designation.value)}
                isLoadingOptions={isFetchingDesignations}
                isDisabled={realTimeProductId == null || isFetchingDesignations}
            />}

        {/* New way - FormSelect with options from fetching providers based on Product and Designation */}
        {isLoading
            ? <Skeleton />
            : <FormSelect
                label={providerLabel}
                options={providerOptions}
                defaultValue={instructionSection?.providerRef}
                onCreateOption={addCustomProvider}
                onBlur={(selection) => new Promise((resolve, reject) => {
                    // If custodyRef is still in the new custodian options list, don't update it
                    // If there is only one option in the new custodian options list, update custodyRef to that option
                    // If there are multiple (or 0) options in the new custodian options list, update custodyRef to null
                    if (custodyOptions.some(opt => opt.value === instructionSection?.custodyRef))
                        return onBlur("providerRef", selection.value.toUpperCase())
                            .then(resolve, reject);

                    if (custodyOptions.length === 1)
                        return patchService([patchReplace("providerRef", selection.value.toUpperCase()), patchReplace("custodyRef", custodyOptions[0].value.toUpperCase())])
                            .then(resolve, reject);

                    return patchService([patchReplace("providerRef", selection.value.toUpperCase()), patchReplace("custodyRef", selection.value.toUpperCase())])
                        .then(resolve, reject);
                })}
                isLoadingOptions={isFetchingProviders}
                isDisabled={realTimeProductId == null || realTimeDesignationId == null || isFetchingProviders || isUninitializedProviders}
                isCreateable
            />}

        {isLoading
            ? <Skeleton />
            : <FormSelect
                label={custodyLabel}
                options={custodyOptions}
                defaultValue={custodyOptions.length === 1
                    ? custodyOptions[0].value
                    : instructionSection?.custodyRef}
                onCreateOption={addCustomCustodian}
                onBlur={(selection) => {
                    const reference = selection.value.toUpperCase();
                    if (selection.custodyId !== null)
                        return patchService([patchReplace("custodyRef", reference), patchReplace("custodyId", selection.custodyId)])

                    return onBlur("custodyRef", reference);
                }}
                isLoadingOptions={isFetchingProviders}
                isDisabled={realTimeProductId == null || realTimeDesignationId == null || isFetchingProviders || isUninitializedProviders || realTimeProviderRef == null}
                isCreateable
            />}

        <span className="mt-auto">
            <FillFromValuationsButton
                forceInvisible={isInvest}
                selectedProductId={realTimeProductId}
                selectedDesignationId={realTimeDesignationId}
                providerRef={realTimeProviderRef}
                custodyRef={custodyOptions.length === 1
                    ? custodyOptions[0].value
                    : instructionSection?.custodyRef}
            />
        </span>

    </GridWrapper>;
}

export default ProductSelection;